import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { usePermissions } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle, Loader2, Recycle, Search, Sparkles, X } from "lucide-react";

// Application rationalisation (backend: routes/rationalisation_routes.py,
// design: docs/design/app-rationalisation.md). This family can be
// rationalised to another one (Mozilla Firefox -> Google Chrome); the target
// then lists the families rationalised into it. When the target is next
// deployed to Test, ALICE builds an uninstall-only app for each of them and
// Intune's "replace" supersedence removes them wherever the target installs.

const CARD_CLASS =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]";

const governancePath = (family) => `/discover/governance/${encodeURIComponent(family)}`;

function UninstallState({ pkg }) {
  if (!pkg) {
    return <span className="text-xs text-white/60">Uninstall-only app built at the next Test deploy</span>;
  }
  if (pkg.intune_app_id) {
    return (
      <span className="text-xs text-green-300 inline-flex items-center gap-1">
        <CheckCircle className="h-3 w-3" /> Uninstall-only app in Intune
      </span>
    );
  }
  return <span className="text-xs text-amber-300">Uninstall-only package built, not yet in Intune</span>;
}

export default function FamilyRationalisationCard({ familyName, getAccessToken, onChange }) {
  const { canManageApps } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // { category, suggestions: [...] }
  const searchSeq = useRef(0);

  const headers = useCallback(async () => {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAccessToken]);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discover/rationalisation/${encodeURIComponent(familyName)}`, {
        headers: await headers(),
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to load rationalisation:", error);
    } finally {
      setLoading(false);
    }
  }, [familyName, headers]);

  useEffect(() => {
    setLoading(true);
    setSuggestions(null);
    load();
  }, [load]);

  // Same-category families (e.g. both "Web Browser") offered first when the
  // picker opens; the free search below still finds anything.
  useEffect(() => {
    if (!picking || suggestions !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${API}/discover/rationalisation-suggestions/${encodeURIComponent(familyName)}`,
          { headers: await headers() },
        );
        if (!cancelled) setSuggestions(response.data);
      } catch (error) {
        console.error("Failed to load rationalisation suggestions:", error);
        if (!cancelled) setSuggestions({ category: null, suggestions: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [picking, suggestions, familyName, headers]);

  useEffect(() => {
    if (!picking || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ search: query.trim(), exclude: familyName, limit: "10" });
        const response = await axios.get(`${API}/discover/dependency-families?${params}`, { headers: await headers() });
        if (seq === searchSeq.current) setResults(response.data.families || []);
      } catch (error) {
        console.error("Family search failed:", error);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, picking, familyName, headers]);

  const save = async (target) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/discover/rationalisation/${encodeURIComponent(familyName)}`,
        { rationalised_to: target },
        { headers: await headers() },
      );
      toast.success(target ? `${familyName} rationalised to ${target}` : `${familyName} is no longer rationalised`);
      setPicking(false);
      setQuery("");
      setSuggestions(null);
      await load();
      if (onChange) onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save rationalisation");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className={CARD_CLASS}>
        <CardContent className="py-6 flex items-center gap-2 text-white/80 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading rationalisation…
        </CardContent>
      </Card>
    );
  }

  const target = data?.rationalised_to;
  const sources = data?.rationalised_from || [];
  const deviceCount = data?.estate?.device_count || 0;

  return (
    <Card className={CARD_CLASS} data-testid="family-rationalisation-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Recycle className="h-5 w-5 text-fuchsia-300" />
          Rationalisation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* This family -> another family */}
        <div>
          <div className="text-sm font-medium text-white mb-1">Rationalised to</div>
          {target ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-fuchsia-600/20 text-fuchsia-300 border border-fuchsia-500">Rationalised</Badge>
                <span className="text-white/80 text-sm">{familyName}</span>
                <ArrowRight className="h-4 w-4 text-white/60" />
                <Link to={governancePath(target)} className="font-medium text-white hover:underline">{target}</Link>
              </div>
              <p className="text-xs text-white/70">
                {deviceCount > 0
                  ? `Still on ${deviceCount.toLocaleString()} device(s). They're removed as ${target} rolls out to them.`
                  : "No longer on the estate."}
                {data?.rationalised_by ? ` Set by ${data.rationalised_by}.` : ""}
              </p>
              {canManageApps && (
                <Button size="sm" variant="outline" disabled={saving} onClick={() => save(null)} data-testid="rationalisation-clear">
                  <X className="h-3 w-3 mr-1" /> Stop rationalising
                </Button>
              )}
            </div>
          ) : sources.length > 0 ? (
            <p className="text-xs text-white/70">
              Other families are rationalised into this one, so it can't be rationalised itself.
            </p>
          ) : picking ? (
            <div className="space-y-2">
              {suggestions === null ? (
                <div className="text-xs text-white/70 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Looking for similar apps…
                </div>
              ) : suggestions.suggestions?.length > 0 ? (
                <div className="space-y-1" data-testid="rationalisation-suggestions">
                  <div className="text-xs font-medium text-fuchsia-200 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Suggested: also {suggestions.category}
                  </div>
                  {suggestions.suggestions.map((sug) => (
                    <button
                      key={sug.family_name}
                      type="button"
                      disabled={saving || sug.full}
                      onClick={() => save(sug.family_name)}
                      className="w-full text-left px-3 py-2 rounded-md bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border border-fuchsia-400/30 disabled:opacity-50"
                      data-testid="rationalisation-suggestion"
                    >
                      <div className="text-sm text-white flex items-center gap-2">
                        {sug.family_name}
                        {sug.replaces_count > 0 && (
                          <Badge className="bg-fuchsia-600/30 text-fuchsia-200 text-[10px] py-0">
                            already replaces {sug.replaces_count}
                          </Badge>
                        )}
                        {sug.packaged && (
                          <Badge variant="outline" className="text-[10px] py-0 text-green-300 border-green-500/50">packaged</Badge>
                        )}
                      </div>
                      <div className="text-xs text-white/60">
                        {(sug.device_count || 0).toLocaleString()} devices
                        {sug.full ? " · already replaces the maximum number of apps" : ""}
                      </div>
                    </button>
                  ))}
                  <div className="text-xs text-white/60 pt-1">Or search for any app:</div>
                </div>
              ) : null}
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-white/60" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for the replacement family…"
                  className="pl-8 bg-white/10 border-white/20 text-white"
                  data-testid="rationalisation-search"
                />
              </div>
              {searching && <div className="text-xs text-white/70">Searching…</div>}
              {results.map((fam) => (
                <button
                  key={fam.family_name}
                  type="button"
                  disabled={saving}
                  onClick={() => save(fam.family_name)}
                  className="w-full text-left px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10"
                >
                  <div className="text-sm text-white">{fam.family_name}</div>
                  <div className="text-xs text-white/60">
                    {fam.publisher ? `${fam.publisher} · ` : ""}
                    {(fam.device_count || 0).toLocaleString()} devices
                  </div>
                </button>
              ))}
              <p className="text-xs text-white/60">
                When the replacement is next deployed to Test, ALICE builds an uninstall-only app for {familyName} and
                sets Intune supersedence to replace it. Its phased rollout also targets the devices that have {familyName}.
              </p>
              <Button size="sm" variant="ghost" onClick={() => { setPicking(false); setQuery(""); }}>Cancel</Button>
            </div>
          ) : canManageApps ? (
            <Button size="sm" variant="outline" onClick={() => setPicking(true)} data-testid="rationalisation-set">
              <Recycle className="h-3 w-3 mr-1" /> Rationalise to another app
            </Button>
          ) : (
            <p className="text-xs text-white/70">Not rationalised.</p>
          )}
        </div>

        {/* Other families -> this family */}
        {sources.length > 0 && (
          <div>
            <div className="text-sm font-medium text-white mb-2">
              Rationalised from ({sources.length} of {data?.max_sources || 5})
            </div>
            <div className="space-y-2">
              {sources.map((src) => (
                <div key={src.family_name} className="px-3 py-2 rounded-md bg-white/5 border border-white/10" data-testid="rationalised-from-row">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={governancePath(src.family_name)} className="font-medium text-white hover:underline">
                      {src.family_name}
                    </Link>
                    <span className="text-xs text-white/80">
                      {src.device_count > 0 ? `${src.device_count.toLocaleString()} device(s) left` : "Removed from the estate"}
                    </span>
                  </div>
                  <UninstallState pkg={src.uninstall_package} />
                </div>
              ))}
            </div>
            <p className="text-xs text-white/60 mt-2">
              Deploying {familyName} to Test adds Intune "replace" supersedence for each of these, and its Phase 1–3
              groups include the devices that still have them.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
